import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';

const API = (path) =>
  fetch(path, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const FUNNEL_LABELS = {
  remortgage: 'Remortgage',
  first_time_buyer: 'First-time buyer',
  buy_to_let: 'Buy-to-let',
};

export default function OwnerFunnelAnalytics() {
  const [funnels, setFunnels] = useState([]);
  const [selected, setSelected] = useState(null); // { apiKey, funnelSlug }
  const [detail, setDetail] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    API('/api/owner/funnel-analytics')
      .then(r => r.json())
      .then(d => {
        const list = d.funnels || [];
        setFunnels(list);
        if (list.length) setSelected({ apiKey: list[0].apiKey, funnelSlug: list[0].funnelSlug });
        setLoadingList(false);
      })
      .catch(() => { setError('Failed to load funnels'); setLoadingList(false); });
  }, []);

  const loadDetail = useCallback((apiKey, funnelSlug) => {
    setLoadingDetail(true);
    API(`/api/owner/funnel-analytics?apiKey=${encodeURIComponent(apiKey)}&funnelSlug=${encodeURIComponent(funnelSlug)}`)
      .then(r => r.json())
      .then(d => { setDetail(d); setLoadingDetail(false); })
      .catch(() => { setError('Failed to load drop-off data'); setLoadingDetail(false); });
  }, []);

  useEffect(() => {
    if (selected) loadDetail(selected.apiKey, selected.funnelSlug);
  }, [selected, loadDetail]);

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  const steps = detail?.steps || [];
  const startSessions = detail?.startSessions || 0;
  const completedLeads = detail?.completedLeads || 0;
  const conversionRate = startSessions > 0 ? Math.round((completedLeads / startSessions) * 1000) / 10 : 0;

  // Find the step with the single biggest drop, to call out visually.
  const biggestDropStep = steps.reduce((worst, s) =>
    (s.droppedFromPrev > (worst?.droppedFromPrev || 0)) ? s : worst, null);

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-main">
        <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>Funnel Analytics</h1>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>
          Step-by-step drop-off for Facebook ad quiz funnels (e.g. remortgage.html) — where visitors abandon before finishing.
        </p>

        {error && <div style={alertRed}>{error}</div>}

        {loadingList ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading…</div>
        ) : funnels.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: '12px', padding: '40px', textAlign: 'center', color: '#9ca3af', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            No funnel activity recorded yet. This fills in automatically once someone visits a tracked funnel page (e.g. leads.gilesfinancialservices.co.uk/remortgage.html).
          </div>
        ) : (
          <>
            {/* Funnel picker — only shown when there's more than one to choose from */}
            {funnels.length > 1 && (
              <div style={{ marginBottom: '20px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {funnels.map(f => {
                  const isActive = selected?.apiKey === f.apiKey && selected?.funnelSlug === f.funnelSlug;
                  return (
                    <button
                      key={`${f.apiKey}-${f.funnelSlug}`}
                      onClick={() => setSelected({ apiKey: f.apiKey, funnelSlug: f.funnelSlug })}
                      style={{
                        padding: '8px 16px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer',
                        border: isActive ? '1px solid #10b981' : '1px solid #d1d5db',
                        background: isActive ? '#ecfdf5' : '#fff',
                        color: isActive ? '#065f46' : '#374151',
                        fontWeight: isActive ? 600 : 400,
                      }}
                    >
                      {f.companyName} · {FUNNEL_LABELS[f.funnelSlug] || f.funnelSlug}
                    </button>
                  );
                })}
              </div>
            )}

            {loadingDetail || !detail ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading…</div>
            ) : (
              <>
                {/* Summary cards */}
                <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
                  {[
                    { label: 'Sessions Started', value: startSessions, color: '#3b82f6' },
                    { label: 'Completed Leads', value: completedLeads, color: '#10b981' },
                    { label: 'Conversion Rate', value: `${conversionRate}%`, color: '#f59e0b' },
                    { label: 'Data Range', value: `${fmtDate(detail.firstSeen)} – ${fmtDate(detail.lastSeen)}`, color: '#6b7280', small: true },
                  ].map(s => (
                    <div key={s.label} style={{ background: '#fff', borderRadius: '8px', padding: '16px 24px', borderLeft: `4px solid ${s.color}`, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', flex: s.small ? '1 1 220px' : 1, minWidth: '160px' }}>
                      <div style={{ fontSize: s.small ? '14px' : '22px', fontWeight: 700, color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {biggestDropStep && biggestDropStep.droppedFromPrev > 0 && (
                  <div style={{ background: '#fef3c7', color: '#92400e', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px' }}>
                    Biggest single-step drop: <strong>{biggestDropStep.droppedFromPrev} of {startSessions}</strong> visitors
                    ({Math.round((biggestDropStep.droppedFromPrev / startSessions) * 100)}%) left at
                    "<strong>{biggestDropStep.stepName || `Step ${biggestDropStep.stepIndex}`}</strong>".
                  </div>
                )}

                {/* Drop-off funnel — horizontal bars, width = % of starting sessions */}
                <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '24px' }}>
                  {steps.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>No step data for this funnel yet.</div>
                  ) : steps.map((s, i) => {
                    const isBigDrop = biggestDropStep && s.stepIndex === biggestDropStep.stepIndex && s.droppedFromPrev > 0;
                    return (
                      <div key={s.stepIndex} style={{ marginBottom: i === steps.length - 1 ? 0 : '18px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '13px' }}>
                          <span style={{ fontWeight: 600, color: '#374151' }}>
                            {s.stepIndex}. {s.stepName || `Step ${s.stepIndex}`}
                          </span>
                          <span style={{ color: '#6b7280' }}>
                            {s.sessions} ({s.pctOfStart}%)
                            {s.droppedFromPrev > 0 && (
                              <span style={{ color: isBigDrop ? '#dc2626' : '#f59e0b', marginLeft: '8px' }}>
                                −{s.droppedFromPrev} dropped
                              </span>
                            )}
                          </span>
                        </div>
                        <div style={{ background: '#f3f4f6', borderRadius: '6px', height: '20px', overflow: 'hidden' }}>
                          <div style={{
                            width: `${s.pctOfStart}%`,
                            height: '100%',
                            borderRadius: '6px',
                            background: isBigDrop ? '#f87171' : 'linear-gradient(90deg, #10b981, #059669)',
                            transition: 'width 0.3s ease',
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}

const alertRed = { background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' };
